import { NodeExtension } from '../../../core/index.js';
import { TextSelection, PluginKey, Plugin } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';
import 'prosemirror-commands';
import 'prosemirror-inputrules';
import { Slice, Fragment } from 'prosemirror-model';
import { createInstance } from '@sailor/i18n-web';

/**
 * 统一样式前缀
 */ var CLASSNAME_PREFIX = "ct";

function _arrayLikeToArray(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _arrayWithoutHoles(arr) {
    if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}
function _iterableToArray(iter) {
    if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _nonIterableSpread() {
    throw new TypeError("Invalid attempt to spread non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _toConsumableArray(arr) {
    return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}
function _unsupportedIterableToArray(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _arrayLikeToArray(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
var noop = function() {};
function getInsertBlockNodes(state, node) {
    var schema = state.schema, selection = state.selection, doc = state.doc;
    var paragraph = schema.nodes.paragraph;
    var $from = selection.$from, $to = selection.$to, to = selection.to;
    var afterNode = $to.nodeAfter;
    var fromNodeGrandParent = $from.node(-1);
    var fromNodeParent = $from.node();
    // 光标在文档尾部
    if (!afterNode) {
        var nodes = [
            node,
            paragraph.create()
        ];
        // 是否在文档尾部后单元格尾部
        var isInTail = to === doc.content.size - 1 || (fromNodeGrandParent && fromNodeGrandParent.lastChild) === fromNodeParent;
        if (isInTail) return nodes;
        // 后面一行是否为文本块节点（即有内联内容的块级节点，schema中定义content为inline或者text的节点例如段落、标题, ）
        var nextNode = to < doc.content.size ? doc.nodeAt(to + 1) : null;
        if (nextNode && !nextNode.isTextblock && !nextNode.isText) {
            // 后面一行为非文本块节点
            return nodes;
        }
    }
    return [
        node
    ];
}
var isParagraph = function(node) {
    var _node_type;
    return (node === null || node === void 0 ? void 0 : (_node_type = node.type) === null || _node_type === void 0 ? void 0 : _node_type.name) === "paragraph";
};
// 插入块处理
function insertBlock(state, node) {
    var options = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : {
        selection: null,
        cursorMove: true
    };
    if (!node.isBlock) {
        throw new Error("必须传入 block node");
    }
    var tr = state.tr, schema = state.schema;
    var selection = options.selection || state.selection;
    var cursorMove = options.cursorMove;
    var _schema_nodes = schema.nodes, table = _schema_nodes.table, note = _schema_nodes.note;
    var from = selection.from, $from = selection.$from;
    // TODO: 为什么需要清除格式
    tr.setStoredMarks([]); // 清除格式
    var insertingNodes = getInsertBlockNodes(state, node);
    // TODO: selection.replace 并不是常用的函数，是否可以替换成 tr.replaceWith
    selection.replace(tr, new Slice(Fragment.from(insertingNodes), 0, 0));
    // transform 1.1 版本会在 replace 过程中增加多余的空行，此处进行判断并清理
    // transform 升级到 1.3 之后可以恢复此段代码的处理，不需要清理多余的空行
    // https://ones.sankuai.com/ones/product/1859/workItem/defect/detail/20632944
    var step = tr.steps[0];
    if (step) {
        var slice = step === null || step === void 0 ? void 0 : step.slice;
        var content = (slice === null || slice === void 0 ? void 0 : slice.content).content;
        var size = content.length;
        // 末尾两个元素是否为 p 标签
        var removeParagraph = size > 2 && isParagraph(content[size - 1]) && isParagraph(content[size - 2]);
        if (removeParagraph) {
            // 如果替换时最后两个node都是 paragraph，说明有多余的空行，需要删除最后一个空行
            var lastPos = tr.mapping.map(step.to);
            tr.delete(lastPos - 2, lastPos);
        }
    }
    tr.setSelection(new TextSelection(tr.doc.resolve(tr.mapping.map(state.selection.from)), tr.doc.resolve(tr.mapping.map(state.selection.to))));
    // TODO:下面这段代码优化
    // 移动光标位置
    if (cursorMove) {
        var nodeBefore = $from.nodeBefore; // 光标前相邻节点
        var offset = nodeBefore ? 2 : 0;
        if (node.type.isLeaf) {
            offset += 1;
        } else if (node.type === note) {
            offset += 4;
        } else if (node.type === table) {
            offset += 3;
        }
        var newPos = selection.constructor.name === "GapCursor" ? from : from + offset;
        tr.setSelection(TextSelection.create(tr.doc, newPos));
    }
    return {
        transaction: tr
    };
}
var classNamePrefix = function(className) {
    var prefix = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : CLASSNAME_PREFIX;
    return "".concat(prefix, "-").concat(className);
};
var execAfterCommands = function(commands) {
    var someThingFn = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : noop;
    return function() {
        for(var _len = arguments.length, cmdArgs = new Array(_len), _key = 0; _key < _len; _key++){
            cmdArgs[_key] = arguments[_key];
        }
        return function(state, dispatch, view) {
            var _ = commands.apply(void 0, _toConsumableArray(cmdArgs))(state, dispatch, view);
            someThingFn(_);
            return _;
        };
    };
};

var findParentNodeClosestToPos = function($pos, predicate) {
    for(var i = $pos.depth; i > 0; i--){
        var node = $pos.node(i);
        if (predicate(node)) {
            return {
                pos: i > 0 ? $pos.before(i) : 0,
                start: $pos.start(i),
                depth: i,
                node: node
            };
        }
    }
};
var findParentNode = function(predicate) {
    return function(param) {
        var $from = param.$from;
        return findParentNodeClosestToPos($from, predicate);
    };
};
// 类型是否可以插入富文本
var canInsertTypes = function(state) {
    var parentNode = state.selection.$from.parent;
    var textType = [
        "status",
        "code_block",
        "note_title",
        "collapse_title"
    ];
    return !textType.includes(parentNode.type.name);
};

var locale = "en";
var version = 16;
var cannot_search_user = "User not found, click space to complete input";
var load_fail_retry = "Failed to load, please try again later";
var pk_avatar = "avatar";
var mention_by_mis_name = "Can @user by name and MIS number";
var searching = "Searching...";
var pk_link_text = "Text";
var insert_blank = "Insert";
var pk_visit_link = "Access the link";
var pk_link_address = "Address";
var pk_link = "Link";
var cancel_blank = "Cancel";
var pk_link_change_tip = "Link text follows the Doc change";
var pk_edit_link = "Edit link";
var pk_link_text_tip = "Input the link text";
var pk_cancel_link = "Cancel link";
var save_blank = "Save";
var pk_link_address_tip = "Input the link address";
var border = "Border";
var pk_insert = "Insert";
var image_link = "Image Link";
var pk_retry = "Retry";
var pk_edit = "Edit";
var width = "Width";
var reupload = "Reupload";
var image_upload_fail1 = "Sorry, upload error!";
var right_address_tip = "Please input right address";
var image_upload_fail3 = "Otherwise, please click button below to retry.";
var image_upload_fail2 = "If the image is moved/pasted from outside, please download and reupload.";
var pk_confirm = "Confirm";
var input_html = "Enter HTML code.";
var formual = "Formual";
var pk_loading = "Loading...";
var click_to_expand = "Click to expand the content";
var pk_cancel = "Cancel";
var input_html_tip = "You can insert HTML code to embed corresponding content on a webpage.";
var input_latex = "Enter Latex code";
var insert_latex_tip = "You can insert Latex code to embed corresponding content within the page.";
var pk_title = "Title";
var wire_frame = "Wireframe";
var fill_in = "Fill";
var input_plantuml = "Enter PlantUML code.";
var border_style = "Border type";
var set_th_line = "Header Column";
var set_th_row = "Header Row";
var table_attr = "Table style";
var cancel_th_line = "Cancel Header Column";
var insert_line_right = "Right Insert Column";
var insert_row = "Delete Rows";
var split_cell = "Split cells";
var delete_line = "Delete Columns";
var pk_no = "No borders";
var input_markdown_tip = "You can insert Markdown code to embed corresponding content within the page.";
var confirm_blank = "Confirm";
var cancel_th_row = "Cancel Header Row";
var insert_row_above = "Insert Row Above";
var delete_table = "Delete";
var input_plantuml_tip = "You can insert PlantUML code to embed corresponding content within the page.";
var border_width = "Border width";
var input_markdown = "Enter Markdown code.";
var merge_cell = "Merge cells";
var set_line_num = "Show Row Number";
var border_color = "Border color";
var insert_line_left = "Left Insert Column";
var insert_row_below = "Insert Row Below";
var cancel_line_num = "Cancel Row Number";
var exit_data_lost_tip = "If you exit, any unsaved data will be lost.";
var if_exit_gantt = "Are you sure to exit the Gantt chart?";
var pk_gantt_loading = "Loading Gantt chart...";
var gantt_load_fail = "Gantt chart loading failed.";
var operate_error = "Invalid operation";
var gantt_keep_work_tip = "Please keep at least one task.";
var save_success = "Save successful";
var gantt_frame_load_fail = "Gantt chart framework failed to load.";
var pk_network_exception = "Network exception";
var save_fail = "Save failed";
var pk_saving = "Saving...";
var gantt_plugin_fail = "Gantt chart editing plugin failed to start.";
var gantt_editing_tip = "Editing Gantt Chart, please wait a moment.";
var pk_discard = "Drop";
var pk_zoom_in = "Zoom in";
var pk_zoom_out = "Zoom out";
var pk_use = "Use";
var draft_edit_tip = "There is a draft that was edited but not saved on {time}. Do you want to continue editing?";
var if_use_local_draft = "Do you use local drafts?";
var pk_load_fail = "Loading failed";
var pk_this_time = "Now";
var choose_time = "Select time";
var pk_hh_mm_ss = "hh:mm:ss A";
var pk_y_d = "MM-DD-YYYY";
var pk_y_d_ts = "MM-DD-YYYY hh:mm:ss A";
var pk_text = "Text";
var h1 = "Heading 1";
var h2 = "Heading 2";
var h3 = "Heading 3";
var h4 = "Heading 4";
var h5 = "Heading 5";
var h6 = "Heading 6";
var pk_catalog = "Catalog";
var page_view_load_fail_click = "Directory tree loading failed. Please click to reload.";
var page_tree_no_sub_doc = "No sub documents found in this directory";
var page_tree_no_perm = "No permission to view the directory tree.";
var pk_load_fail_retry = "Loading failed, please click to reload.";
var loading2 = "Loading";
var page_tree_deleted = "The directory tree has been deleted.";
var pk_pause = "Pause";
var pk_mute = "Mute";
var pk_untitled = "Untitled";
var pk_play = "Play";
var adjust_volume = "Adjust volume";
var download_fail_retry = "Download failed please retry";
var pk_download = "Download";
var get_file_fail = "Failed to get file";
var pk_network_fail = "Network connection failed";
var pk_upload_fail = "Upload failed";
var audio_not_support_tip = "This browser does not support the HTML5 audio tag.";
var pk_volume = "Volume";
var browser_not_support_tip = "This browser does not support the HTML5 video tag.";
var pk_zoom_in2 = "Zoom in";
var video_upload_tip = "Video is being transcoded, if the file is large, it may take a long time. Please wait patiently...";
var pk_video = "Video";
var pk_zoom_out2 = "Zoom out";
var space_update_fail_tip = "Space update acquisition failed. Click to retry.";
var pk_recent_update = "Recent Updates";
var show_more = "Show More";
var no_space_update = "No Updates in Space.";
var pk_restore = "Restore";
var en_parker = {
    locale: locale,
    version: version,
    cannot_search_user: cannot_search_user,
    load_fail_retry: load_fail_retry,
    pk_avatar: pk_avatar,
    mention_by_mis_name: mention_by_mis_name,
    searching: searching,
    pk_link_text: pk_link_text,
    insert_blank: insert_blank,
    pk_visit_link: pk_visit_link,
    pk_link_address: pk_link_address,
    pk_link: pk_link,
    cancel_blank: cancel_blank,
    pk_link_change_tip: pk_link_change_tip,
    pk_edit_link: pk_edit_link,
    pk_link_text_tip: pk_link_text_tip,
    pk_cancel_link: pk_cancel_link,
    save_blank: save_blank,
    pk_link_address_tip: pk_link_address_tip,
    border: border,
    pk_insert: pk_insert,
    image_link: image_link,
    pk_retry: pk_retry,
    pk_edit: pk_edit,
    width: width,
    reupload: reupload,
    image_upload_fail1: image_upload_fail1,
    right_address_tip: right_address_tip,
    image_upload_fail3: image_upload_fail3,
    image_upload_fail2: image_upload_fail2,
    pk_confirm: pk_confirm,
    input_html: input_html,
    formual: formual,
    pk_loading: pk_loading,
    click_to_expand: click_to_expand,
    pk_cancel: pk_cancel,
    input_html_tip: input_html_tip,
    input_latex: input_latex,
    insert_latex_tip: insert_latex_tip,
    pk_title: pk_title,
    wire_frame: wire_frame,
    fill_in: fill_in,
    input_plantuml: input_plantuml,
    border_style: border_style,
    set_th_line: set_th_line,
    set_th_row: set_th_row,
    table_attr: table_attr,
    cancel_th_line: cancel_th_line,
    insert_line_right: insert_line_right,
    insert_row: insert_row,
    split_cell: split_cell,
    delete_line: delete_line,
    pk_no: pk_no,
    input_markdown_tip: input_markdown_tip,
    confirm_blank: confirm_blank,
    cancel_th_row: cancel_th_row,
    insert_row_above: insert_row_above,
    delete_table: delete_table,
    input_plantuml_tip: input_plantuml_tip,
    border_width: border_width,
    input_markdown: input_markdown,
    merge_cell: merge_cell,
    set_line_num: set_line_num,
    border_color: border_color,
    insert_line_left: insert_line_left,
    insert_row_below: insert_row_below,
    cancel_line_num: cancel_line_num,
    exit_data_lost_tip: exit_data_lost_tip,
    if_exit_gantt: if_exit_gantt,
    pk_gantt_loading: pk_gantt_loading,
    gantt_load_fail: gantt_load_fail,
    operate_error: operate_error,
    gantt_keep_work_tip: gantt_keep_work_tip,
    save_success: save_success,
    gantt_frame_load_fail: gantt_frame_load_fail,
    pk_network_exception: pk_network_exception,
    save_fail: save_fail,
    pk_saving: pk_saving,
    gantt_plugin_fail: gantt_plugin_fail,
    gantt_editing_tip: gantt_editing_tip,
    pk_discard: pk_discard,
    pk_zoom_in: pk_zoom_in,
    pk_zoom_out: pk_zoom_out,
    pk_use: pk_use,
    draft_edit_tip: draft_edit_tip,
    if_use_local_draft: if_use_local_draft,
    pk_load_fail: pk_load_fail,
    pk_this_time: pk_this_time,
    choose_time: choose_time,
    pk_hh_mm_ss: pk_hh_mm_ss,
    pk_y_d: pk_y_d,
    pk_y_d_ts: pk_y_d_ts,
    pk_text: pk_text,
    h1: h1,
    h2: h2,
    h3: h3,
    h4: h4,
    h5: h5,
    h6: h6,
    pk_catalog: pk_catalog,
    page_view_load_fail_click: page_view_load_fail_click,
    page_tree_no_sub_doc: page_tree_no_sub_doc,
    page_tree_no_perm: page_tree_no_perm,
    pk_load_fail_retry: pk_load_fail_retry,
    loading2: loading2,
    page_tree_deleted: page_tree_deleted,
    pk_pause: pk_pause,
    pk_mute: pk_mute,
    pk_untitled: pk_untitled,
    pk_play: pk_play,
    adjust_volume: adjust_volume,
    download_fail_retry: download_fail_retry,
    pk_download: pk_download,
    get_file_fail: get_file_fail,
    pk_network_fail: pk_network_fail,
    pk_upload_fail: pk_upload_fail,
    audio_not_support_tip: audio_not_support_tip,
    pk_volume: pk_volume,
    browser_not_support_tip: browser_not_support_tip,
    pk_zoom_in2: pk_zoom_in2,
    video_upload_tip: video_upload_tip,
    pk_video: pk_video,
    pk_zoom_out2: pk_zoom_out2,
    space_update_fail_tip: space_update_fail_tip,
    pk_recent_update: pk_recent_update,
    show_more: show_more,
    no_space_update: no_space_update,
    pk_restore: pk_restore
};

var locale$1 = "zh";
var version$1 = 16;
var cannot_search_user$1 = "未搜索到用户，点击空格完成输入";
var load_fail_retry$1 = "加载失败，请稍后重试";
var pk_avatar$1 = "头像";
var mention_by_mis_name$1 = "可通过姓名及mis号@用户";
var searching$1 = "搜索中...";
var pk_link_text$1 = "链接文字";
var insert_blank$1 = "插 入";
var pk_link$1 = "链接";
var pk_link_address$1 = "链接地址";
var pk_visit_link$1 = "访问链接";
var cancel_blank$1 = "取 消";
var pk_link_change_tip$1 = "链接文字跟随文档标题变更";
var pk_edit_link$1 = "编辑链接";
var pk_cancel_link$1 = "取消链接";
var pk_link_text_tip$1 = "请输入链接文字";
var save_blank$1 = "保 存";
var pk_link_address_tip$1 = "请输入链接地址";
var border$1 = "边框";
var pk_insert$1 = "插入";
var image_link$1 = "图片链接";
var pk_edit$1 = "编辑";
var pk_retry$1 = "重试";
var width$1 = "宽度";
var reupload$1 = "重新上传";
var image_upload_fail1$1 = "抱歉，图片上传失败！";
var image_upload_fail3$1 = "否则，请点击下方按钮进行重试。";
var right_address_tip$1 = "请输入正确的地址";
var image_upload_fail2$1 = "如果是从外部拖动、粘贴过来的图片，请下载原图并重新上传。";
var pk_confirm$1 = "确定";
var input_html$1 = "输入HTML代码";
var formual$1 = "公式";
var pk_loading$1 = "正在加载中...";
var pk_cancel$1 = "取消";
var click_to_expand$1 = "点击展开内容";
var input_html_tip$1 = "可以插入HTML代码，以在页面内嵌入相应内容：";
var insert_latex_tip$1 = "可以插入Latex代码，以在页面内嵌入相应内容：";
var input_latex$1 = "输入Latex代码";
var pk_title$1 = "标题";
var wire_frame$1 = "线框";
var fill_in$1 = "填充";
var input_plantuml$1 = "輸入PlantUML代碼";
var border_style$1 = "边框样式";
var set_th_row$1 = "设置表头行";
var set_th_line$1 = "设置表头列";
var table_attr$1 = "表格属性";
var cancel_th_line$1 = "取消表头列";
var insert_row$1 = "删除行";
var insert_line_right$1 = "右侧插入列";
var split_cell$1 = "拆分单元格";
var delete_line$1 = "删除列";
var confirm_blank$1 = "确 定";
var input_markdown_tip$1 = "可以插入Markdown代码，以在页面内嵌入相应内容：";
var pk_no$1 = "无";
var cancel_th_row$1 = "取消表头行";
var insert_row_above$1 = "上方插入行";
var delete_table$1 = "删除表格";
var input_plantuml_tip$1 = "可以插入PlantUML代碼，以在頁面內嵌入相應內容";
var border_width$1 = "边框宽度";
var input_markdown$1 = "输入Markdown代码";
var merge_cell$1 = "合并单元格";
var set_line_num$1 = "设置行号列";
var border_color$1 = "边框颜色";
var insert_line_left$1 = "左侧插入列";
var insert_row_below$1 = "下方插入行";
var cancel_line_num$1 = "取消行号列";
var if_exit_gantt$1 = "确定退出甘特图?";
var exit_data_lost_tip$1 = "退出后，尚未保存的数据将丢失。";
var gantt_load_fail$1 = "甘特图载入失败";
var pk_gantt_loading$1 = "甘特图载入中...";
var operate_error$1 = "您的操作有误";
var gantt_keep_work_tip$1 = "请至少保留一个任务";
var gantt_frame_load_fail$1 = "甘特图框架加载失败";
var save_success$1 = "保存成功";
var save_fail$1 = "保存失败";
var pk_network_exception$1 = "网络异常";
var pk_saving$1 = "正在保存中...";
var gantt_plugin_fail$1 = "甘特图编辑插件启动失败";
var gantt_editing_tip$1 = "正在编辑甘特图，请稍候再试噢。";
var pk_discard$1 = "废弃";
var pk_zoom_in$1 = "放大查看";
var pk_zoom_out$1 = "缩小查看";
var pk_use$1 = "使用";
var draft_edit_tip$1 = "存在一份于&nbsp;{time}&nbsp;进行了编辑但未保存的草稿，是否继续编辑";
var pk_load_fail$1 = "加载失败";
var if_use_local_draft$1 = "是否使用本地草稿？";
var pk_this_time$1 = "此刻";
var choose_time$1 = "选择时间";
var pk_hh_mm_ss$1 = "HH:mm:ss";
var pk_y_d$1 = "YYYY-MM-DD";
var pk_y_d_ts$1 = "YYYY-MM-DD HH:mm:ss";
var pk_text$1 = "正文";
var h1$1 = "标题 1";
var h2$1 = "标题 2";
var h3$1 = "标题 3";
var h4$1 = "标题 4";
var h5$1 = "标题 5";
var h6$1 = "标题 6";
var pk_catalog$1 = "目录";
var page_view_load_fail_click$1 = "目录树加载失败，请点击重新加载";
var page_tree_no_sub_doc$1 = "该目录下暂无子文档";
var page_tree_no_perm$1 = "无权查看该目录树";
var pk_load_fail_retry$1 = "加载失败，请点击重新加载";
var loading2$1 = "正在加载";
var page_tree_deleted$1 = "该目录树已被删除";
var pk_pause$1 = "暂停";
var pk_mute$1 = "静音";
var pk_untitled$1 = "未命名";
var pk_play$1 = "播放";
var adjust_volume$1 = "音量调节";
var download_fail_retry$1 = "下载失败，请重试";
var get_file_fail$1 = "文件获取失败";
var pk_download$1 = "下载";
var pk_network_fail$1 = "网络连接失败";
var pk_upload_fail$1 = "上传失败";
var audio_not_support_tip$1 = "本浏览器不支持 HTML5 audio 标签";
var browser_not_support_tip$1 = "本浏览器不支持 HTML5 video 标签";
var pk_volume$1 = "音量";
var pk_zoom_in2$1 = "放大";
var pk_video$1 = "视频";
var video_upload_tip$1 = "视频正在转码中，如文件较大可能时间较长，请耐心等待…";
var pk_zoom_out2$1 = "缩小";
var space_update_fail_tip$1 = "空间更新获取失败，点击重试";
var pk_recent_update$1 = "最近更新";
var show_more$1 = "显示更多";
var no_space_update$1 = "空间暂时没有动态～";
var pk_restore$1 = "恢复默认";
var zh_parker = {
    locale: locale$1,
    version: version$1,
    cannot_search_user: cannot_search_user$1,
    load_fail_retry: load_fail_retry$1,
    pk_avatar: pk_avatar$1,
    mention_by_mis_name: mention_by_mis_name$1,
    searching: searching$1,
    pk_link_text: pk_link_text$1,
    insert_blank: insert_blank$1,
    pk_link: pk_link$1,
    pk_link_address: pk_link_address$1,
    pk_visit_link: pk_visit_link$1,
    cancel_blank: cancel_blank$1,
    pk_link_change_tip: pk_link_change_tip$1,
    pk_edit_link: pk_edit_link$1,
    pk_cancel_link: pk_cancel_link$1,
    pk_link_text_tip: pk_link_text_tip$1,
    save_blank: save_blank$1,
    pk_link_address_tip: pk_link_address_tip$1,
    border: border$1,
    pk_insert: pk_insert$1,
    image_link: image_link$1,
    pk_edit: pk_edit$1,
    pk_retry: pk_retry$1,
    width: width$1,
    reupload: reupload$1,
    image_upload_fail1: image_upload_fail1$1,
    image_upload_fail3: image_upload_fail3$1,
    right_address_tip: right_address_tip$1,
    image_upload_fail2: image_upload_fail2$1,
    pk_confirm: pk_confirm$1,
    input_html: input_html$1,
    formual: formual$1,
    pk_loading: pk_loading$1,
    pk_cancel: pk_cancel$1,
    click_to_expand: click_to_expand$1,
    input_html_tip: input_html_tip$1,
    insert_latex_tip: insert_latex_tip$1,
    input_latex: input_latex$1,
    pk_title: pk_title$1,
    wire_frame: wire_frame$1,
    fill_in: fill_in$1,
    input_plantuml: input_plantuml$1,
    border_style: border_style$1,
    set_th_row: set_th_row$1,
    set_th_line: set_th_line$1,
    table_attr: table_attr$1,
    cancel_th_line: cancel_th_line$1,
    insert_row: insert_row$1,
    insert_line_right: insert_line_right$1,
    split_cell: split_cell$1,
    delete_line: delete_line$1,
    confirm_blank: confirm_blank$1,
    input_markdown_tip: input_markdown_tip$1,
    pk_no: pk_no$1,
    cancel_th_row: cancel_th_row$1,
    insert_row_above: insert_row_above$1,
    delete_table: delete_table$1,
    input_plantuml_tip: input_plantuml_tip$1,
    border_width: border_width$1,
    input_markdown: input_markdown$1,
    merge_cell: merge_cell$1,
    set_line_num: set_line_num$1,
    border_color: border_color$1,
    insert_line_left: insert_line_left$1,
    insert_row_below: insert_row_below$1,
    cancel_line_num: cancel_line_num$1,
    if_exit_gantt: if_exit_gantt$1,
    exit_data_lost_tip: exit_data_lost_tip$1,
    gantt_load_fail: gantt_load_fail$1,
    pk_gantt_loading: pk_gantt_loading$1,
    operate_error: operate_error$1,
    gantt_keep_work_tip: gantt_keep_work_tip$1,
    gantt_frame_load_fail: gantt_frame_load_fail$1,
    save_success: save_success$1,
    save_fail: save_fail$1,
    pk_network_exception: pk_network_exception$1,
    pk_saving: pk_saving$1,
    gantt_plugin_fail: gantt_plugin_fail$1,
    gantt_editing_tip: gantt_editing_tip$1,
    pk_discard: pk_discard$1,
    pk_zoom_in: pk_zoom_in$1,
    pk_zoom_out: pk_zoom_out$1,
    pk_use: pk_use$1,
    draft_edit_tip: draft_edit_tip$1,
    pk_load_fail: pk_load_fail$1,
    if_use_local_draft: if_use_local_draft$1,
    pk_this_time: pk_this_time$1,
    choose_time: choose_time$1,
    pk_hh_mm_ss: pk_hh_mm_ss$1,
    pk_y_d: pk_y_d$1,
    pk_y_d_ts: pk_y_d_ts$1,
    pk_text: pk_text$1,
    h1: h1$1,
    h2: h2$1,
    h3: h3$1,
    h4: h4$1,
    h5: h5$1,
    h6: h6$1,
    pk_catalog: pk_catalog$1,
    page_view_load_fail_click: page_view_load_fail_click$1,
    page_tree_no_sub_doc: page_tree_no_sub_doc$1,
    page_tree_no_perm: page_tree_no_perm$1,
    pk_load_fail_retry: pk_load_fail_retry$1,
    loading2: loading2$1,
    page_tree_deleted: page_tree_deleted$1,
    pk_pause: pk_pause$1,
    pk_mute: pk_mute$1,
    pk_untitled: pk_untitled$1,
    pk_play: pk_play$1,
    adjust_volume: adjust_volume$1,
    download_fail_retry: download_fail_retry$1,
    get_file_fail: get_file_fail$1,
    pk_download: pk_download$1,
    pk_network_fail: pk_network_fail$1,
    pk_upload_fail: pk_upload_fail$1,
    audio_not_support_tip: audio_not_support_tip$1,
    browser_not_support_tip: browser_not_support_tip$1,
    pk_volume: pk_volume$1,
    pk_zoom_in2: pk_zoom_in2$1,
    pk_video: pk_video$1,
    video_upload_tip: video_upload_tip$1,
    pk_zoom_out2: pk_zoom_out2$1,
    space_update_fail_tip: space_update_fail_tip$1,
    pk_recent_update: pk_recent_update$1,
    show_more: show_more$1,
    no_space_update: no_space_update$1,
    pk_restore: pk_restore$1
};

var locale$2 = "zh-HK";
var version$2 = 16;
var cannot_search_user$2 = "未能找到用户，請按空格鍵完成輸入";
var load_fail_retry$2 = "載入失敗，請稍後重試。";
var pk_avatar$2 = "頭像";
var mention_by_mis_name$2 = "可通過姓名及MIS號碼@用戶";
var searching$2 = "搜尋中...";
var pk_link_text$2 = "鏈接文字";
var insert_blank$2 = "插 入";
var pk_visit_link$2 = "访问鏈接";
var pk_link_address$2 = "鏈接地址";
var pk_link$2 = "鏈接";
var cancel_blank$2 = "取 消";
var pk_link_change_tip$2 = "鏈接文字跟隨文件標題變更";
var pk_edit_link$2 = "編輯鏈接";
var pk_link_text_tip$2 = "請輸入鏈接文字";
var pk_cancel_link$2 = "取消鏈接";
var save_blank$2 = "保 存";
var pk_link_address_tip$2 = "請輸入鏈接地址";
var border$2 = "邊框";
var pk_insert$2 = "插入";
var image_link$2 = "圖片鏈接";
var pk_retry$2 = "重試";
var pk_edit$2 = "編輯";
var width$2 = "寬度";
var reupload$2 = "重新上傳";
var image_upload_fail1$2 = "抱歉，圖片上傳失敗！";
var right_address_tip$2 = "請輸入正確的地址";
var image_upload_fail3$2 = "否則，請按下方按鈕進行重試。";
var image_upload_fail2$2 = "如果您從外部拖動、粘貼過來的圖片，請下載原圖並重新上傳。";
var pk_confirm$2 = "確定";
var input_html$2 = "輸入HTML代碼";
var formual$2 = "公式";
var pk_loading$2 = "正在載入中...";
var pk_cancel$2 = "取消";
var click_to_expand$2 = "點選展開內容";
var input_html_tip$2 = "可以插入HTML代碼，以在頁面內嵌入相應內容：";
var insert_latex_tip$2 = "可以插入Latex代碼，以在頁面內嵌入相應內容：";
var input_latex$2 = "输入LaTeX代码";
var pk_title$2 = "標題";
var wire_frame$2 = "線框";
var fill_in$2 = "填充";
var input_plantuml$2 = "輸入PlantUML程式碼";
var border_style$2 = "邊框樣式";
var set_th_row$2 = "設置表頭行";
var set_th_line$2 = "設置表頭列";
var table_attr$2 = "表格屬性";
var cancel_th_line$2 = "取消表頭列";
var insert_row$2 = "删除行";
var insert_line_right$2 = "右側插入列";
var split_cell$2 = "拆分儲存格";
var delete_line$2 = "删除列";
var pk_no$2 = "無";
var confirm_blank$2 = "確 定";
var input_markdown_tip$2 = "可以插入Markdown代碼，以在頁面內嵌入相應內容：";
var cancel_th_row$2 = "取消表頭行";
var insert_row_above$2 = "上方插入行";
var delete_table$2 = "刪除表格";
var input_plantuml_tip$2 = "可以插入PlantUML代碼，以在頁面內嵌入相應內容：";
var border_width$2 = "邊框寬度";
var input_markdown$2 = "輸入Markdown代碼";
var merge_cell$2 = "合併儲存格";
var set_line_num$2 = "設置行号列";
var border_color$2 = "邊框顏色";
var insert_line_left$2 = "左側插入列";
var insert_row_below$2 = "下方插入行";
var cancel_line_num$2 = "取消行号列";
var if_exit_gantt$2 = "確定退出甘特圖?";
var exit_data_lost_tip$2 = "退出後，尚未儲存的數據將會遺失。";
var pk_gantt_loading$2 = "甘特圖載入中...";
var gantt_load_fail$2 = "甘特圖載入失敗";
var operate_error$2 = "您的操作有誤";
var gantt_keep_work_tip$2 = "請至少保留一個任務";
var save_success$2 = "保存成功";
var gantt_frame_load_fail$2 = "甘特圖框架載入失敗";
var save_fail$2 = "保存失敗";
var pk_network_exception$2 = "網絡異常";
var pk_saving$2 = "正在儲存中...";
var gantt_plugin_fail$2 = "甘特圖編輯插件啟動失敗。";
var gantt_editing_tip$2 = "正在編輯甘特圖，請稍後再試唷。";
var pk_discard$2 = "廢棄";
var pk_zoom_in$2 = "放大查看";
var pk_zoom_out$2 = "縮小查看";
var pk_use$2 = "使用";
var draft_edit_tip$2 = "存在一份于&nbsp;{time}&nbsp;进行了编辑但未保存的草稿，是否继續編輯";
var pk_load_fail$2 = "加載失敗";
var if_use_local_draft$2 = "是否使用本地草稿？";
var pk_this_time$2 = "此刻";
var choose_time$2 = "選擇時間";
var pk_hh_mm_ss$2 = "HH:mm:ss";
var pk_y_d$2 = "YYYY-MM-DD";
var pk_y_d_ts$2 = "YYYY-MM-DD HH:mm:ss";
var pk_text$2 = "正文";
var h1$2 = "標題 1";
var h2$2 = "標題 2";
var h3$2 = "標題 3";
var h4$2 = "標題 4";
var h5$2 = "標題 5";
var h6$2 = "標題 6";
var pk_catalog$2 = "目錄";
var page_view_load_fail_click$2 = "目錄樹加載失敗，請點擊重新加載。";
var page_tree_no_sub_doc$2 = "該目錄下暫無子文檔。";
var page_tree_no_perm$2 = "無權查看該目錄樹";
var pk_load_fail_retry$2 = "加載失敗，請點擊重新加載。";
var loading2$2 = "正在載入";
var page_tree_deleted$2 = "該目錄樹已被刪除";
var pk_pause$2 = "暫停";
var pk_mute$2 = "靜音";
var pk_untitled$2 = "未命名";
var pk_play$2 = "播放";
var adjust_volume$2 = "音量調節";
var download_fail_retry$2 = "下載失敗，請重試";
var pk_download$2 = "下載";
var get_file_fail$2 = "文件獲取失敗";
var pk_network_fail$2 = "網絡連接失敗";
var pk_upload_fail$2 = "上載失敗";
var audio_not_support_tip$2 = "本瀏覽器不支援 HTML5 audio 標籤";
var pk_volume$2 = "音量";
var browser_not_support_tip$2 = "本瀏覽器不支援 HTML5 video 標籤";
var pk_zoom_in2$2 = "放大";
var video_upload_tip$2 = "視頻正在轉碼中，如果文件較大可能需要較長的時間，請耐心等待...";
var pk_video$2 = "視頻";
var pk_zoom_out2$2 = "縮小";
var space_update_fail_tip$2 = "空間更新獲取失敗，點擊重試";
var pk_recent_update$2 = "最近更新";
var show_more$2 = "顯示更多";
var no_space_update$2 = "空間暫時沒有動態～";
var pk_restore$2 = "恢復預設";
var zhHK_parker = {
    locale: locale$2,
    version: version$2,
    cannot_search_user: cannot_search_user$2,
    load_fail_retry: load_fail_retry$2,
    pk_avatar: pk_avatar$2,
    mention_by_mis_name: mention_by_mis_name$2,
    searching: searching$2,
    pk_link_text: pk_link_text$2,
    insert_blank: insert_blank$2,
    pk_visit_link: pk_visit_link$2,
    pk_link_address: pk_link_address$2,
    pk_link: pk_link$2,
    cancel_blank: cancel_blank$2,
    pk_link_change_tip: pk_link_change_tip$2,
    pk_edit_link: pk_edit_link$2,
    pk_link_text_tip: pk_link_text_tip$2,
    pk_cancel_link: pk_cancel_link$2,
    save_blank: save_blank$2,
    pk_link_address_tip: pk_link_address_tip$2,
    border: border$2,
    pk_insert: pk_insert$2,
    image_link: image_link$2,
    pk_retry: pk_retry$2,
    pk_edit: pk_edit$2,
    width: width$2,
    reupload: reupload$2,
    image_upload_fail1: image_upload_fail1$2,
    right_address_tip: right_address_tip$2,
    image_upload_fail3: image_upload_fail3$2,
    image_upload_fail2: image_upload_fail2$2,
    pk_confirm: pk_confirm$2,
    input_html: input_html$2,
    formual: formual$2,
    pk_loading: pk_loading$2,
    pk_cancel: pk_cancel$2,
    click_to_expand: click_to_expand$2,
    input_html_tip: input_html_tip$2,
    insert_latex_tip: insert_latex_tip$2,
    input_latex: input_latex$2,
    pk_title: pk_title$2,
    wire_frame: wire_frame$2,
    fill_in: fill_in$2,
    input_plantuml: input_plantuml$2,
    border_style: border_style$2,
    set_th_row: set_th_row$2,
    set_th_line: set_th_line$2,
    table_attr: table_attr$2,
    cancel_th_line: cancel_th_line$2,
    insert_row: insert_row$2,
    insert_line_right: insert_line_right$2,
    split_cell: split_cell$2,
    delete_line: delete_line$2,
    pk_no: pk_no$2,
    confirm_blank: confirm_blank$2,
    input_markdown_tip: input_markdown_tip$2,
    cancel_th_row: cancel_th_row$2,
    insert_row_above: insert_row_above$2,
    delete_table: delete_table$2,
    input_plantuml_tip: input_plantuml_tip$2,
    border_width: border_width$2,
    input_markdown: input_markdown$2,
    merge_cell: merge_cell$2,
    set_line_num: set_line_num$2,
    border_color: border_color$2,
    insert_line_left: insert_line_left$2,
    insert_row_below: insert_row_below$2,
    cancel_line_num: cancel_line_num$2,
    if_exit_gantt: if_exit_gantt$2,
    exit_data_lost_tip: exit_data_lost_tip$2,
    pk_gantt_loading: pk_gantt_loading$2,
    gantt_load_fail: gantt_load_fail$2,
    operate_error: operate_error$2,
    gantt_keep_work_tip: gantt_keep_work_tip$2,
    save_success: save_success$2,
    gantt_frame_load_fail: gantt_frame_load_fail$2,
    save_fail: save_fail$2,
    pk_network_exception: pk_network_exception$2,
    pk_saving: pk_saving$2,
    gantt_plugin_fail: gantt_plugin_fail$2,
    gantt_editing_tip: gantt_editing_tip$2,
    pk_discard: pk_discard$2,
    pk_zoom_in: pk_zoom_in$2,
    pk_zoom_out: pk_zoom_out$2,
    pk_use: pk_use$2,
    draft_edit_tip: draft_edit_tip$2,
    pk_load_fail: pk_load_fail$2,
    if_use_local_draft: if_use_local_draft$2,
    pk_this_time: pk_this_time$2,
    choose_time: choose_time$2,
    pk_hh_mm_ss: pk_hh_mm_ss$2,
    pk_y_d: pk_y_d$2,
    pk_y_d_ts: pk_y_d_ts$2,
    pk_text: pk_text$2,
    h1: h1$2,
    h2: h2$2,
    h3: h3$2,
    h4: h4$2,
    h5: h5$2,
    h6: h6$2,
    pk_catalog: pk_catalog$2,
    page_view_load_fail_click: page_view_load_fail_click$2,
    page_tree_no_sub_doc: page_tree_no_sub_doc$2,
    page_tree_no_perm: page_tree_no_perm$2,
    pk_load_fail_retry: pk_load_fail_retry$2,
    loading2: loading2$2,
    page_tree_deleted: page_tree_deleted$2,
    pk_pause: pk_pause$2,
    pk_mute: pk_mute$2,
    pk_untitled: pk_untitled$2,
    pk_play: pk_play$2,
    adjust_volume: adjust_volume$2,
    download_fail_retry: download_fail_retry$2,
    pk_download: pk_download$2,
    get_file_fail: get_file_fail$2,
    pk_network_fail: pk_network_fail$2,
    pk_upload_fail: pk_upload_fail$2,
    audio_not_support_tip: audio_not_support_tip$2,
    pk_volume: pk_volume$2,
    browser_not_support_tip: browser_not_support_tip$2,
    pk_zoom_in2: pk_zoom_in2$2,
    video_upload_tip: video_upload_tip$2,
    pk_video: pk_video$2,
    pk_zoom_out2: pk_zoom_out2$2,
    space_update_fail_tip: space_update_fail_tip$2,
    pk_recent_update: pk_recent_update$2,
    show_more: show_more$2,
    no_space_update: no_space_update$2,
    pk_restore: pk_restore$2
};

// This file was auto generated by resource-sync tools.
var resources = {
    "en": {
        parker: en_parker
    },
    "zh": {
        parker: zh_parker
    },
    "zh-HK": {
        parker: zhHK_parker
    }
};

function _classCallCheck(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
var I18nManager = /*#__PURE__*/ function() {
    function I18nManager() {
        _classCallCheck(this, I18nManager);
    }
    I18nManager.getInstance = function getInstance() {
        if (this.instance === null) {
            this.instance = createInstance();
            this.instance.init({
                // lng: 'zh',
                fallbackLng: "zh",
                ns: [
                    "parker"
                ],
                defaultNS: "parker",
                resources: resources,
                interpolation: {
                    escapeValue: false
                },
                react: {
                    useSuspense: false
                }
            });
            window.parkerI18nClient = this.instance;
        }
        return this.instance;
    };
    return I18nManager;
}();
I18nManager.instance = null;
var i18nClient = I18nManager.getInstance();

// 查找 title 节点的次数
var SEARCH_COUNT = 4;
// 判断当前节点是否为展开宏的 title 节点
var titleCompare = function(item) {
    var _item_type;
    return (item === null || item === void 0 ? void 0 : (_item_type = item.type) === null || _item_type === void 0 ? void 0 : _item_type.name) === "collapse_title";
};
// 查找当前展开宏内部的 title 节点
function getTitleSelection(tr) {
    var titlePos;
    var addNum = 1;
    var titleSelection;
    var getTitle = function getTitle1() {
        titlePos = tr.selection.$from.pos + addNum++;
        titleSelection = TextSelection.create(tr.doc, titlePos);
        if (titleSelection.$from.nodeBefore && titleCompare(titleSelection.$from.nodeBefore)) {
            titleSelection = TextSelection.create(tr.doc, titlePos - 1);
            return titleSelection;
        }
        var collapseTitle = findParentNode(titleCompare)(titleSelection);
        if (!collapseTitle && addNum < SEARCH_COUNT) {
            // 目前最多找三次就可以找到
            return getTitle();
        }
        return titleSelection;
    };
    return getTitle();
}
function insertCollapse() {
    return function(state, dispatch, view) {
        var schema = state.schema;
        var _schema_nodes = schema.nodes, collapse = _schema_nodes.collapse, collapse_title = _schema_nodes.collapse_title, collapse_content = _schema_nodes.collapse_content;
        var titleNode = collapse_title.createAndFill();
        var contentNode = collapse_content.createAndFill();
        var collapseNode = collapse.create(null, Fragment.from([
            titleNode,
            contentNode
        ]));
        var transaction = insertBlock(state, collapseNode).transaction;
        // 需要确定 pos 在 collapse_title 里面，否则会出现位置错误的现象
        var newSelection = getTitleSelection(transaction);
        transaction.setSelection(newSelection);
        transaction.insertText(i18nClient.t("click_to_expand", "点击展开内容"), newSelection.from);
        dispatch(transaction.scrollIntoView());
        view.focus();
        return true;
    };
}

function styleInject(css, ref) {
    if (ref === void 0) ref = {};
    var insertAt = ref.insertAt;
    if (!css || typeof document === "undefined") {
        return;
    }
    var head = document.head || document.getElementsByTagName("head")[0];
    var style = document.createElement("style");
    style.type = "text/css";
    if (insertAt === "top") {
        if (head.firstChild) {
            head.insertBefore(style, head.firstChild);
        } else {
            head.appendChild(style);
        }
    } else {
        head.appendChild(style);
    }
    if (style.styleSheet) {
        style.styleSheet.cssText = css;
    } else {
        style.appendChild(document.createTextNode(css));
    }
}

var css_248z = '.ct-collapse{min-width:267px;margin:8px 0;padding:8px;border-radius:2px}.ct-collapse.active>.ct-collapse-title{padding-bottom:8px}.ct-collapse.active>.ct-collapse-title .ct-collapse-title-text:before{transform:rotate(0deg)}.ct-collapse.active>.ct-collapse-content{display:block;height:auto;padding:8px 24px;border-radius:2px}.ct-collapse .ct-collapse-title{line-height:16px}.ct-collapse .ct-collapse-title>.ct-collapse-title-text{position:relative;padding-left:24px;color:#4d4d4d;font-family:PingFangSC-Medium;font-size:13px;vertical-align:top}.ct-collapse .ct-collapse-title>.ct-collapse-title-text:before{position:absolute;top:2px;left:3px;content:"";width:16px;height:16px;background:url("data:image/svg+xml,%3C%3Fxml version%3D%221.0%22 standalone%3D%22no%22%3F%3E%3C!DOCTYPE svg PUBLIC %22-%2F%2FW3C%2F%2FDTD SVG 1.1%2F%2FEN%22        %22http%3A%2F%2Fwww.w3.org%2FGraphics%2FSVG%2F1.1%2FDTD%2Fsvg11.dtd%22%3E%3Csvg t%3D%221526953416889%22 class%3D%22icon%22 style%3D%22%22 viewBox%3D%220 0 1024 1024%22 version%3D%221.1%22 xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22     p-id%3D%223453%22 xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22 width%3D%228%22 height%3D%228%22%3E    %3Cdefs%3E        %3Cstyle type%3D%22text%2Fcss%22%3E%3C%2Fstyle%3E    %3C%2Fdefs%3E    %3Cpath d%3D%22M375.966782 310.02559401L28.2073 642.809787a89.339756 89.339756 0 0 0 0 130.169816 99.323282 99.323282 0 0 0 136.057537 0L512.024319 440.323405 859.7838 773.107597a99.323282 99.323282 0 0 0 136.057538 0 89.339756 89.339756 0 0 0 0-130.169817l-347.759482-332.784192-65.916869-59.389179a105.722978 105.722978 0 0 0-140.281336 0l-65.916869 59.389179z%22          fill%3D%22%23747272%22 p-id%3D%223454%22%3E%3C%2Fpath%3E%3C%2Fsvg%3E") no-repeat 50%;transform:rotate(-180deg);transition:transform .2s;cursor:pointer}.ct-collapse>.ct-collapse-content{height:.1px;padding:0;background:transparent;overflow:hidden}.ProseMirror .ct-collapse{overflow:hidden}.ProseMirror .ct-collapse.active>.ct-collapse-content{display:block;height:auto;border-radius:2px}.ProseMirror .ct-collapse>.ct-collapse-content{display:none;overflow:unset}.ProseMirror .ct-collapse:hover{background-color:#f6f6f6}.ProseMirror .ct-collapse:hover>.ct-collapse-content{background-color:#fff}.ProseMirror .ct-collapse-show.ct-collapse{background-color:#f6f6f6;border:1px solid #ddd}.ProseMirror .ct-collapse-show.ct-collapse>.ct-collapse-content{background-color:#f6f6f6}.ProseMirror .ct-collapse-show.active>.ct-collapse-content{background-color:#fff;border:1px solid #ddd}.ProseMirror-selectednode.ct-collapse{border:1px solid #1c6cdc}.ProseMirror .ct-collapse.ProseMirror-selectednode{-webkit-user-select:none;-moz-user-select:none;-ms-user-select:none;user-select:none}.pm-body-wrapper .ct-collapse-title-text{cursor:pointer}';
styleInject(css_248z);

var AFTER_INSERT = "after_insert";

function _assertThisInitialized(self) {
    if (self === void 0) {
        throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
    }
    return self;
}
function _classCallCheck$1(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
function _defineProperties(target, props) {
    for(var i = 0; i < props.length; i++){
        var descriptor = props[i];
        descriptor.enumerable = descriptor.enumerable || false;
        descriptor.configurable = true;
        if ("value" in descriptor) descriptor.writable = true;
        Object.defineProperty(target, descriptor.key, descriptor);
    }
}
function _createClass(Constructor, protoProps, staticProps) {
    if (protoProps) _defineProperties(Constructor.prototype, protoProps);
    if (staticProps) _defineProperties(Constructor, staticProps);
    return Constructor;
}
function _defineProperty(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _getPrototypeOf(o) {
    _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
        return o.__proto__ || Object.getPrototypeOf(o);
    };
    return _getPrototypeOf(o);
}
function _inherits(subClass, superClass) {
    if (typeof superClass !== "function" && superClass !== null) {
        throw new TypeError("Super expression must either be null or a function");
    }
    subClass.prototype = Object.create(superClass && superClass.prototype, {
        constructor: {
            value: subClass,
            writable: true,
            configurable: true
        }
    });
    if (superClass) _setPrototypeOf(subClass, superClass);
}
function _objectSpread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _defineProperty(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _objectSpreadProps(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _possibleConstructorReturn(self, call) {
    if (call && (_typeof(call) === "object" || typeof call === "function")) {
        return call;
    }
    return _assertThisInitialized(self);
}
function _setPrototypeOf(o, p) {
    _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
        o.__proto__ = p;
        return o;
    };
    return _setPrototypeOf(o, p);
}
var _typeof = function(obj) {
    "@swc/helpers - typeof";
    return obj && typeof Symbol !== "undefined" && obj.constructor === Symbol ? "symbol" : typeof obj;
};
function _isNativeReflectConstruct() {
    if (typeof Reflect === "undefined" || !Reflect.construct) return false;
    if (Reflect.construct.sham) return false;
    if (typeof Proxy === "function") return true;
    try {
        Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function() {}));
        return true;
    } catch (e) {
        return false;
    }
}
function _createSuper(Derived) {
    var hasNativeReflectConstruct = _isNativeReflectConstruct();
    return function _createSuperInternal() {
        var Super = _getPrototypeOf(Derived), result;
        if (hasNativeReflectConstruct) {
            var NewTarget = _getPrototypeOf(this).constructor;
            result = Reflect.construct(Super, arguments, NewTarget);
        } else {
            result = Super.apply(this, arguments);
        }
        return _possibleConstructorReturn(this, result);
    };
}
var pluginKey = new PluginKey("collapse");
var CLS_PREFIX = classNamePrefix("collapse");
var nodeCompare = function(item) {
    return item.type && item.type.name === "collapse";
};
var CollapseExtension = /*#__PURE__*/ function(NodeExtension) {
    _inherits(CollapseExtension, NodeExtension);
    var _super = _createSuper(CollapseExtension);
    function CollapseExtension() {
        _classCallCheck$1(this, CollapseExtension);
        return _super.apply(this, arguments);
    }
    var _proto = CollapseExtension.prototype;
    _proto.plugin = function plugin(param) {
        var getState = param.getState;
        return new Plugin({
            key: pluginKey,
            props: {
                handleClick: function(view, pos, e) {
                    var target = e.target;
                    var isTitle = target.classList.contains("".concat(CLS_PREFIX, "-title-text"));
                    var dis = e.clientX - target.getBoundingClientRect().left;
                    var isCollapseWidget = isTitle && dis > 2 && dis < 20;
                    if (isCollapseWidget) {
                        var state = view.state, dispatch = view.dispatch;
                        var tr = state.tr;
                        var collapse = findParentNode(nodeCompare)({
                            $from: tr.doc.resolve(pos)
                        });
                        if (collapse) {
                            var attrs = collapse.node.attrs;
                            dispatch(tr.setNodeMarkup(collapse.pos, null, _objectSpreadProps(_objectSpread({}, attrs), {
                                active: !attrs.active
                            })).scrollIntoView());
                        }
                    }
                    return false;
                },
                decorations: function(state) {
                    var selection = state.selection;
                    var collapse = findParentNode(function(item) {
                        return item.type && item.type.name === "collapse";
                    })(selection);
                    if (collapse) {
                        var attrs = {
                            class: "".concat(CLS_PREFIX, "-show")
                        };
                        var deco = Decoration.node(collapse.pos, collapse.pos + collapse.node.nodeSize, attrs);
                        return DecorationSet.create(state.doc, [
                            deco
                        ]);
                    }
                    return DecorationSet.empty;
                },
                transformPasted: function transformPasted(slice) {
                    var state = getState();
                    var schema = state.schema;
                    if (!canInsertTypes(state)) {
                        var content = slice.content;
                        var text = content.textBetween(0, content.size);
                        var textNode = schema.text(text);
                        return new Slice(Fragment.from([
                            textNode
                        ]), 0, 0);
                    }
                    return slice;
                }
            }
        });
    };
    _proto.commands = function commands(param) {
        var getState = param.getState, editorEventEmitter = param.editorEventEmitter;
        return {
            insert: execAfterCommands(insertCollapse, function() {
                return editorEventEmitter.emit(AFTER_INSERT, {
                    braceWidth: 300
                });
            }),
            isDisabled: function() {
                return function() {
                    return !canInsertTypes(getState());
                };
            }
        };
    };
    _proto.transformPastedHTML = function transformPastedHTML(html) {
        if (html.includes("ct-collapse")) {
            // 如果粘贴展开宏时，结构中没有 collapse_content 或 ct—collapse-title 则手动添加上，todo: 之后在复制逻辑里实现
            var createDom = document.createElement("div");
            createDom.innerHTML = html;
            var collapseDoms = createDom.querySelectorAll(".ct-collapse");
            collapseDoms.forEach(function(collapseDom) {
                if (!collapseDom.querySelector(".ct-collapse-content")) {
                    var collapseContentDom = document.createElement("div");
                    collapseContentDom.className = "ct-collapse-content";
                    collapseContentDom.innerHTML = "<p></p>";
                    collapseDom.appendChild(collapseContentDom);
                } else if (!collapseDom.querySelector(".ct-collapse-title")) {
                    var collapseTitleDom = document.createElement("div");
                    collapseTitleDom.className = "ct-collapse-title";
                    collapseTitleDom.innerHTML = '<span class="ct-collapse-title-text" spellcheck="false"></span>';
                    collapseDom.insertBefore(collapseTitleDom, collapseDom.children[0]);
                }
            });
            html = createDom.innerHTML;
        }
        var collapseTestReg = /class="ct-collapse-[^>]*?data-pm-slice="/;
        var collapseExecReg = /class="ct-collapse-[^>]*>([\s\S]*?)<\/div>/;
        if (html.includes('data-pm-slice="')) {
            if (!html.includes('data-pm-slice="none"')) {
                if (collapseTestReg.test(html)) {
                    html = collapseExecReg.exec(html)[1];
                }
            }
        }
        return html;
    };
    _createClass(CollapseExtension, [
        {
            key: "name",
            get: function get() {
                return "collapse";
            }
        }
    ]);
    return CollapseExtension;
}(NodeExtension);

export { CollapseExtension, pluginKey };
//# sourceMappingURL=index.js.map
